// Camera frames without a playing <video>.
//
// An iPhone WKWebView whose host app leaves allowsInlineMediaPlayback off (the Salesforce
// mobile app's login web view when "use the native browser for user authentication on iOS"
// is not in effect) ignores playsinline and forces every playing <video> into the native
// fullscreen player. Ours is hidden behind the preview canvas, so the user is left staring
// at a blank fullscreen screen. The page cannot change that setting, so on those web views
// the frames are read in a dedicated worker instead (MediaStreamTrackProcessor, exposed by
// WebKit in dedicated workers only, iOS 18+) and posted back as ImageBitmaps — nothing ever
// plays, so nothing goes fullscreen.
//
// Inlined as a blob URL rather than a separate bundle entry so neither webpack
// (react-scripts 5) nor Jest has to understand `new URL(..., import.meta.url)`.
export const WORKER_SOURCE = `
// A single createImageBitmap failure can be transient (memory pressure, a frame torn down
// mid-read); only this many in a row end the session as a camera error.
const MAX_BITMAP_FAILURES = 3;
let track = null;
let reader = null;
let inFlight = false;
let bitmapFailures = 0;
self.onmessage = async (event) => {
  const data = event.data || {};
  if (data.type === "ack") {
    inFlight = false;
    return;
  }
  if (data.type === "stop") {
    try { if (reader) await reader.cancel(); } catch (e) {}
    try { if (track) track.stop(); } catch (e) {}
    return;
  }
  if (data.type !== "start") return;
  track = data.track;
  if (typeof MediaStreamTrackProcessor === "undefined") {
    // The transferred clone is ours now; left running it keeps the camera light on.
    try { track.stop(); } catch (e) {}
    self.postMessage({ type: "unsupported" });
    return;
  }
  track.addEventListener("ended", () => self.postMessage({ type: "ended" }));
  try {
    reader = new MediaStreamTrackProcessor({ track }).readable.getReader();
    for (;;) {
      const { value: frame, done } = await reader.read();
      if (done) break;
      // One frame in flight at a time: the page acks after drawing it. Frames that arrive
      // meanwhile are dropped instead of queuing ImageBitmaps faster than they are consumed.
      if (inFlight) {
        frame.close();
        continue;
      }
      inFlight = true;
      try {
        const bitmap = await createImageBitmap(frame);
        // Camera frames arrive in sensor orientation; rotation/flip say how to render them,
        // which a <video> applies on its own. createImageBitmap does not, unless a future
        // WebKit starts to: a bitmap already in the rotated shape is sent as-is. WebKit does
        // not expose rotation/flip on VideoFrame at all; null tells the page to infer them.
        let rotation = typeof frame.rotation === "number" ? frame.rotation : null;
        let flip = typeof frame.flip === "boolean" ? frame.flip : null;
        const rawW = frame.visibleRect ? frame.visibleRect.width : frame.codedWidth;
        const rawH = frame.visibleRect ? frame.visibleRect.height : frame.codedHeight;
        const quarterTurn = rotation === 90 || rotation === 270;
        if (quarterTurn && rawW !== rawH && bitmap.width === rawH && bitmap.height === rawW) {
          rotation = 0;
          flip = false;
        }
        bitmapFailures = 0;
        self.postMessage({ type: "frame", bitmap, rotation, flip }, [bitmap]);
      } catch (err) {
        inFlight = false;
        bitmapFailures += 1;
        if (bitmapFailures >= MAX_BITMAP_FAILURES) {
          self.postMessage({ type: "error", message: String((err && err.message) || err) });
          break;
        }
      } finally {
        frame.close();
      }
    }
  } catch (err) {
    self.postMessage({ type: "error", message: String((err && err.message) || err) });
  }
};
`;

export type FrameSourceMode = "video" | "worker";

// `?faceFrameSource=worker|video` forces a mode, so the worker path can be exercised in
// plain iPhone/Mac Safari without going through the Salesforce app.
function forcedMode(): FrameSourceMode | null {
  try {
    const value = new URLSearchParams(window.location.search).get("faceFrameSource");
    return value === "worker" || value === "video" ? value : null;
  } catch {
    return null;
  }
}

// The Salesforce mobile app's own web view on an iPhone. The Mobile SDK prefixes the web
// view's user agent with its own tokens, e.g.
//   SalesforceMobileSDK/12.0.1 iOS/18.0.1 (iPhone) Chatter/252.010(…) Hybrid … Mozilla/5.0
//   (iPhone; …) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 … Salesforce1/252.010(…)
// When the app authenticates in the native browser instead (ASWebAuthenticationSession),
// the page sees Safari's user agent and keeps the <video> path. Other in-app browsers (LINE,
// Facebook, Instagram, Teams) play inline and are deliberately left on <video> too. iPad
// is excluded: allowsInlineMediaPlayback defaults to true there.
export function isSalesforceIPhoneWebView(userAgent: string = navigator.userAgent): boolean {
  return /iPhone|iPod/.test(userAgent) && /SalesforceMobileSDK\/|Salesforce1\/|Chatter\//.test(userAgent);
}

export function preferredFrameSourceMode(): FrameSourceMode {
  const forced = forcedMode();
  if (forced) return forced;
  return isSalesforceIPhoneWebView() ? "worker" : "video";
}

// Snap to 0/90/180/270; anything unexpected (missing, NaN) means no rotation.
export function normalizeRotation(value: unknown): number {
  const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return (((Math.round(n / 90) * 90) % 360) + 360) % 360;
}

// Size of the bitmap once `rotation` has been applied.
export function orientedSize(
  width: number,
  height: number,
  rotation: number
): { width: number; height: number } {
  return rotation === 90 || rotation === 270
    ? { width: height, height: width }
    : { width, height };
}

// Orientation for a frame that carries no rotation/flip metadata (WebKit). WebKit reports
// the track's width/height already transposed to the rendered orientation, so a bitmap
// whose shape disagrees with them needs a quarter turn: 90° clockwise. No flip: on an
// iPhone the front camera's frames arrive un-mirrored, exactly like the <video> path's —
// flipping them made the captured image a mirror image of the one registered from
// Safari/PC. A matching shape (landscape phone, desktop cameras) is drawn as-is.
export function inferOrientation(
  bitmapWidth: number,
  bitmapHeight: number,
  settings: MediaTrackSettings
): FrameOrientation {
  const { width, height } = settings;
  const none = { rotation: 0, flip: false };
  if (!width || !height || width === height || bitmapWidth === bitmapHeight) return none;
  if ((width > height) === (bitmapWidth > bitmapHeight)) return none;
  return { rotation: 90, flip: false };
}

type DrawContext = Pick<
  CanvasRenderingContext2D,
  "setTransform" | "translate" | "scale" | "rotate" | "drawImage"
>;

// Draw `bitmap` upright onto a canvas already sized to orientedSize(...). Transforms
// compose right-to-left on the drawn pixels: rotate first, then flip.
export function drawOrientedFrame(
  ctx: DrawContext,
  bitmap: ImageBitmap,
  { rotation, flip }: FrameOrientation
): void {
  const size = orientedSize(bitmap.width, bitmap.height, rotation);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.translate(size.width / 2, size.height / 2);
  if (flip) ctx.scale(-1, 1);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

function safeSettings(track: MediaStreamTrack): MediaTrackSettings {
  try {
    return track.getSettings?.() ?? {};
  } catch {
    return {};
  }
}

export interface WorkerFrameSource {
  // Resolves on the first frame; rejects when the worker cannot read the track (no
  // MediaStreamTrackProcessor, track not transferable, processing error) or when stop()
  // comes first.
  firstFrame: Promise<void>;
  stop: () => void;
}

// How to turn the raw bitmap into the intended picture: rotate clockwise by `rotation`
// degrees, then mirror horizontally when `flip` is set (WebCodecs VideoFrame semantics).
export interface FrameOrientation {
  rotation: number;
  flip: boolean;
}

interface Handlers {
  onFrame: (bitmap: ImageBitmap, orientation: FrameOrientation) => void;
  onEnded: () => void;
  onError: (message: string) => void;
}

// Start reading `track` in a worker. A clone is transferred so the caller keeps the
// original: its "ended"/"mute" listeners stay meaningful and stopCamera() still owns it.
export function startWorkerFrameSource(
  track: MediaStreamTrack,
  handlers: Handlers
): WorkerFrameSource {
  const blobUrl = URL.createObjectURL(
    new Blob([WORKER_SOURCE], { type: "text/javascript" })
  );
  let worker: Worker;
  try {
    worker = new Worker(blobUrl);
  } catch (err) {
    URL.revokeObjectURL(blobUrl);
    throw err;
  }
  // WebKit can fail to start a blob worker whose URL is revoked right after new Worker()
  // (vitejs/vite#19565, microsoft/playwright#33794), so the URL lives until the worker
  // has spoken, failed, or been stopped.
  let blobUrlLive = true;
  const revokeBlobUrl = () => {
    if (!blobUrlLive) return;
    blobUrlLive = false;
    URL.revokeObjectURL(blobUrl);
  };

  let stopped = false;
  let gotFirstFrame = false;
  let resolveFirst!: () => void;
  let rejectFirst!: (err: Error) => void;
  const firstFrame = new Promise<void>((resolve, reject) => {
    resolveFirst = resolve;
    rejectFirst = reject;
  });
  // A caller that has already given up (stop() during startup) must not surface as an
  // unhandled rejection; callers that await firstFrame still see it reject.
  firstFrame.catch(() => {});

  const fail = (message: string) => {
    if (!gotFirstFrame) rejectFirst(new Error(message));
    else handlers.onError(message);
  };

  worker.onmessage = (event: MessageEvent) => {
    revokeBlobUrl();
    if (stopped) {
      event.data?.bitmap?.close?.();
      return;
    }
    const data = event.data || {};
    switch (data.type) {
      case "frame": {
        const bitmap: ImageBitmap = data.bitmap;
        // The worker holds every later frame until this ack, so it must go out even when
        // drawing throws — otherwise the preview freezes on a still-live track, with no
        // "ended" and no error to recover from.
        try {
          if (!gotFirstFrame) {
            gotFirstFrame = true;
            resolveFirst();
          }
          handlers.onFrame(
            bitmap,
            typeof data.rotation === "number"
              ? { rotation: normalizeRotation(data.rotation), flip: Boolean(data.flip) }
              : inferOrientation(bitmap.width, bitmap.height, safeSettings(track))
          );
        } catch (err) {
          bitmap.close?.();
          console.warn("[face-capture] dropped a worker frame:", err);
        } finally {
          worker.postMessage({ type: "ack" });
        }
        break;
      }
      case "ended":
        handlers.onEnded();
        break;
      case "unsupported":
        fail("MediaStreamTrackProcessor unavailable in worker");
        break;
      case "error":
        fail(`worker frame error: ${data.message}`);
        break;
    }
  };
  worker.onerror = (event: ErrorEvent) => {
    revokeBlobUrl();
    fail(`worker error: ${event.message}`);
  };

  const clone = track.clone();
  try {
    worker.postMessage({ type: "start", track: clone }, [clone as unknown as Transferable]);
  } catch (err: any) {
    clone.stop();
    rejectFirst(new Error(`MediaStreamTrack not transferable: ${err?.message ?? err}`));
  }

  return {
    firstFrame,
    stop: () => {
      if (stopped) return;
      stopped = true;
      revokeBlobUrl();
      // Settle a pending startup now instead of leaving it to the caller's timeout, which
      // would otherwise fire long after this session was replaced.
      if (!gotFirstFrame) rejectFirst(new Error("worker frame source stopped"));
      worker.postMessage({ type: "stop" });
      // Give the worker a moment to stop its track before tearing it down; terminating
      // alone does not reliably release the camera.
      window.setTimeout(() => worker.terminate(), 500);
    },
  };
}
